import {Dialog, Transition} from '@headlessui/react';
import {Fragment} from 'react';
import crossIcon from '../../../../assets/images/icons/cross.svg';
import {useI18n} from '../../providers/I18nProvider';
import InButtonLoader from '../InButtonLoader';

interface Props {
	show: boolean;
	message: string;
	onChoice: (choice: boolean) => void;
	showTitle?: boolean;
	title?: string;
	isLoading?: boolean;
	confirmText?: string;
	cancelText?: string;
}

export default function ConfirmModal({
	message,
	show,
	onChoice,
	showTitle = true,
	title,
	confirmText,
	cancelText,
	isLoading = false,
}: Props) {
	const {i18n} = useI18n();

	const titleText = showTitle ? title ?? i18n('COMMON.SURE') : null;

	const handleNo = () => onChoice(false);
	const handleYes = () => onChoice(true);

	return (
		<Transition appear show={show} as={Fragment}>
			<Dialog as='div' className='relative z-40' onClose={handleNo}>
				<Transition.Child
					as={Fragment}
					enter='ease-out duration-300'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leave='ease-in duration-200'
					leaveFrom='opacity-100'
					leaveTo='opacity-0'
				>
					<div className='fixed inset-0 bg-black bg-opacity-25' />
				</Transition.Child>

				<div className='fixed inset-0 overflow-y-auto'>
					<div className='flex min-h-full items-center justify-center p-4 text-center'>
						<Transition.Child
							as={Fragment}
							enter='ease-out duration-300'
							enterFrom='opacity-0 scale-95'
							enterTo='opacity-100 scale-100'
							leave='ease-in duration-200'
							leaveFrom='opacity-100 scale-100'
							leaveTo='opacity-0 scale-95'
						>
							<Dialog.Panel className='w-auto transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all'>
								<div className='flex items-center justify-between py-6 px-4'>
									<button
										className='p-1 ml-auto border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none'
										onClick={handleNo}
									>
										<img src={crossIcon} alt='close modal' />
									</button>
								</div>
								<div className='p-4 text-center'>
									<h2 className='text-black-10 font-bold mb-4 text-[20px]'>{titleText}</h2>
									<span className='text-black-40 font-medium mb-4'>{message}</span>
								</div>
								<div className='mt-7 mb-7  pr-2 pl-2 flex justify-center  gap-4 sm:gap-4  min-w-[373px] sm:min-w-[400px] ml-[16px] mr-[16px]'>
									<div className='w-full flex my-auto'>
										<button
											type='button'
											className=' w-full inline-flex justify-center border border-queen-blue bg-primary/20 py-3 font-poppins font-medium text-black rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 text-center'
											onClick={handleYes}
										>
											<InButtonLoader isLoading={isLoading}>{confirmText ?? i18n('COMMON.YES')}</InButtonLoader>
										</button>
									</div>
									<div className='w-full flex mr-[3.75rem] sm:mr-0'>
										<button
											type='button'
											className='w-full inline-flex justify-center border border-transparent bg-queen-blue py-3 font-poppins font-medium text-white rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 shadow-primary text-center'
											onClick={handleNo}
										>
											{cancelText ?? i18n('COMMON.NO')}
										</button>
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
